import type { ResourceMap } from '@hostesa/i18n';

/**
 * i18n resources for the `knowledge` namespace (documents, FAQ, gaps, search).
 *
 * The coordinator merges `resources` into the app i18n instance. Non-English
 * locales reuse the English copy as a temporary placeholder so the key-parity
 * check passes; translators replace the strings without changing the key shape.
 */

export const namespace = 'knowledge' as const;

const en = {
  pageTitle: 'Knowledge',
  pageDescription:
    'The facts your AI receptionist uses to answer guests — documents, FAQs, and open questions.',
  eyebrow: 'Intelligence',
  tabs: {
    documents: 'Documents',
    faqs: 'FAQs',
    gaps: 'Open questions',
    search: 'Search',
  },
  search: {
    label: 'Search the knowledge base',
    placeholder: 'Ask a question, e.g. "What time is check-in?"',
    submit: 'Search',
    empty: 'No matching knowledge yet. Try different wording, or add an FAQ.',
    scoreLabel: 'Relevance',
    resultsTitle: 'Results',
  },
  documents: {
    title: 'Knowledge documents',
    description: 'Source documents that ground the AI. Approve a document to make it answerable.',
    empty: 'No knowledge documents yet.',
    loadHint: 'Open a document by id to review and approve it.',
    inputLabel: 'Document id',
    inputPlaceholder: 'Paste a document id to open',
    open: 'Open document',
    columnTitle: 'Title',
    columnCategory: 'Category',
    columnLanguage: 'Language',
    columnSource: 'Source',
    columnStatus: 'Status',
    columnUpdated: 'Updated',
    sourceLink: 'View source',
    active: 'Active',
    inactive: 'Inactive',
  },
  faqs: {
    title: 'FAQs',
    description: 'Approved question-and-answer pairs the AI can quote directly.',
    empty: 'No FAQs yet. Answer an open question to create one.',
    loading: 'Loading FAQs…',
    error: 'FAQs could not be loaded. Try again shortly.',
    question: 'Question',
    answer: 'Approved answer',
    category: 'Category',
    language: 'Language',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
  },
  gaps: {
    title: 'Open questions',
    description:
      'Questions guests asked that the AI could not confidently answer. Triage each one.',
    empty: 'No open questions — the AI answered everything it was asked.',
    columnQuestion: 'Question',
    columnLanguage: 'Language',
    columnScore: 'Retrieval score',
    columnStatus: 'Status',
    columnDetected: 'Detected',
    noPreview: 'No question text captured',
    resolve: 'Link to FAQ',
    dismiss: 'Dismiss',
    duplicate: 'Mark duplicate',
    answer: 'Answer',
    resolveFaqIdLabel: 'FAQ id to link',
    resolveFaqIdPlaceholder: 'Paste the FAQ id',
    answerTitle: 'Answer this question',
    answerQuestionLabel: 'Question',
    answerLabel: 'Approved answer',
    answerCategoryLabel: 'Category',
    answerLanguageLabel: 'Language',
    answerSubmit: 'Save answer',
    cancel: 'Cancel',
  },
  status: {
    uploaded: 'Uploaded',
    queued_for_parsing: 'Queued',
    parsing: 'Parsing',
    parsed: 'Parsed',
    pending_review: 'Pending review',
    approved: 'Approved',
    archived: 'Archived',
    failed: 'Failed',
    needs_answer: 'Needs answer',
    answered: 'Answered',
    dismissed: 'Dismissed',
    duplicate: 'Duplicate',
  },
  category: {
    hotel_profile: 'Hotel profile',
    rooms: 'Rooms',
    offers: 'Offers',
    policies: 'Policies',
    services: 'Services',
  },
  actions: {
    approve: 'Approve',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    archive: 'Archive',
    approving: 'Approving…',
    saving: 'Saving…',
  },
  states: {
    loading: 'Loading knowledge…',
    errorTitle: 'Could not load knowledge',
    errorDescription: 'Something went wrong while loading the knowledge base. Please try again.',
    retry: 'Retry',
  },
  citations: {
    title: 'Sources',
    confidence: 'Confidence',
    source: 'Source',
  },
  approvedBy: 'Recorded by the system on approval',
};

export const resources: ResourceMap = {
  en: { [namespace]: en },
  bg: { [namespace]: en },
  de: { [namespace]: en },
  fr: { [namespace]: en },
};

export default { namespace, resources };
